const PARTNER_BINDING_MODE = {
  REQUIRED_SINGLE: "REQUIRED_SINGLE",
  OPTIONAL_SINGLE: "OPTIONAL_SINGLE",
  REQUIRED_MULTI: "REQUIRED_MULTI",
  OPTIONAL_MULTI: "OPTIONAL_MULTI",
};

const CHECK_POLICY = {
  NONE: "NONE",
  CHECKIN_ONLY: "CHECKIN_ONLY",
  CHECKOUT_ONLY: "CHECKOUT_ONLY",
  CHECKIN_AND_CHECKOUT: "CHECKIN_AND_CHECKOUT",
};

const STEP_MODE = {
  DISABLED: "DISABLED",
  ENABLED: "ENABLED",
};

const IMAGE_REQUIREMENT = {
  OPTIONAL: "OPTIONAL",
  REQUIRED: "REQUIRED",
};

const APPROVAL_MODE = {
  AUTOMATIC: "AUTOMATIC",
  ADMIN_APPROVAL: "ADMIN_APPROVAL",
};

const FIELD_MODE = {
  HIDDEN: "HIDDEN",
  OPTIONAL: "OPTIONAL",
  REQUIRED: "REQUIRED",
};

export const TASK_FIELD_CODES = {
  BANCO_NEGOCIADO: "BANCO_NEGOCIADO",
  PRODUTO_NEGOCIADO: "PRODUTO_NEGOCIADO",
  CONVENIO_NEGOCIADO: "CONVENIO_NEGOCIADO",
  VALOR_PRODUZIDO: "VALOR_PRODUZIDO",
  CONCORRENTE: "CONCORRENTE",
};

export const PARTNER_BINDING_OPTIONS = [
  { value: PARTNER_BINDING_MODE.REQUIRED_SINGLE, label: "1 parceiro obrigatorio" },
  { value: PARTNER_BINDING_MODE.OPTIONAL_SINGLE, label: "0 ou 1 parceiro" },
  { value: PARTNER_BINDING_MODE.REQUIRED_MULTI, label: "1 ou mais parceiros" },
  { value: PARTNER_BINDING_MODE.OPTIONAL_MULTI, label: "0 ou mais parceiros" },
];

export const CHECK_POLICY_OPTIONS = [
  { value: CHECK_POLICY.NONE, label: "Sem check" },
  { value: CHECK_POLICY.CHECKIN_ONLY, label: "Check-in" },
  { value: CHECK_POLICY.CHECKOUT_ONLY, label: "Check-out" },
  { value: CHECK_POLICY.CHECKIN_AND_CHECKOUT, label: "Check-in e check-out" },
];

export const STEP_MODE_OPTIONS = [
  { value: STEP_MODE.DISABLED, label: "Desabilitado" },
  { value: STEP_MODE.ENABLED, label: "Habilitado" },
];

export const IMAGE_REQUIREMENT_OPTIONS = [
  { value: IMAGE_REQUIREMENT.OPTIONAL, label: "Imagem opcional" },
  { value: IMAGE_REQUIREMENT.REQUIRED, label: "Imagem obrigatoria" },
];

export const APPROVAL_MODE_OPTIONS = [
  { value: APPROVAL_MODE.AUTOMATIC, label: "Automatico" },
  { value: APPROVAL_MODE.ADMIN_APPROVAL, label: "Aprovação admin" },
];

export const FIELD_MODE_OPTIONS = [
  { value: FIELD_MODE.HIDDEN, label: "Oculto" },
  { value: FIELD_MODE.OPTIONAL, label: "Opcional" },
  { value: FIELD_MODE.REQUIRED, label: "Obrigatorio" },
];

export function createDefaultTipoRules() {
  return {
    partnerBindingMode: PARTNER_BINDING_MODE.REQUIRED_SINGLE,
    checkPolicy: CHECK_POLICY.NONE,
    fallback: {
      mode: STEP_MODE.ENABLED,
      imageRequirement: IMAGE_REQUIREMENT.REQUIRED,
      approvalMode: APPROVAL_MODE.ADMIN_APPROVAL,
    },
    evidence: {
      mode: STEP_MODE.DISABLED,
      imageRequirement: IMAGE_REQUIREMENT.OPTIONAL,
      approvalMode: APPROVAL_MODE.AUTOMATIC,
    },
  };
}

export function createDefaultMotivoFields() {
  return {
    fields: {
      [TASK_FIELD_CODES.BANCO_NEGOCIADO]: FIELD_MODE.HIDDEN,
      [TASK_FIELD_CODES.PRODUTO_NEGOCIADO]: FIELD_MODE.HIDDEN,
      [TASK_FIELD_CODES.CONVENIO_NEGOCIADO]: FIELD_MODE.HIDDEN,
      [TASK_FIELD_CODES.VALOR_PRODUZIDO]: FIELD_MODE.HIDDEN,
      [TASK_FIELD_CODES.CONCORRENTE]: FIELD_MODE.HIDDEN,
    },
  };
}

export function normalizeTipoRules(rawRules) {
  const defaults = createDefaultTipoRules();
  return {
    partnerBindingMode: rawRules?.partnerBindingMode || defaults.partnerBindingMode,
    checkPolicy: rawRules?.checkPolicy || defaults.checkPolicy,
    fallback: {
      mode: rawRules?.fallback?.mode || defaults.fallback.mode,
      imageRequirement: rawRules?.fallback?.imageRequirement || defaults.fallback.imageRequirement,
      approvalMode: rawRules?.fallback?.approvalMode || defaults.fallback.approvalMode,
    },
    evidence: {
      mode: rawRules?.evidence?.mode || defaults.evidence.mode,
      imageRequirement: rawRules?.evidence?.imageRequirement || defaults.evidence.imageRequirement,
      approvalMode: rawRules?.evidence?.approvalMode || defaults.evidence.approvalMode,
    },
  };
}

export function normalizeMotivoFields(rawConfig) {
  const defaults = createDefaultMotivoFields();
  const fields = { ...defaults.fields };
  Object.values(TASK_FIELD_CODES).forEach((code) => {
    fields[code] = rawConfig?.fields?.[code] || defaults.fields[code];
  });
  return { fields };
}

export function getPartnerBindingMode(tipoOrRules) {
  const rules = tipoOrRules?.regras ? tipoOrRules.regras : tipoOrRules;
  return normalizeTipoRules(rules).partnerBindingMode;
}

export function allowsNoPartner(tipoOrRules) {
  const mode = getPartnerBindingMode(tipoOrRules);
  return mode === PARTNER_BINDING_MODE.OPTIONAL_SINGLE || mode === PARTNER_BINDING_MODE.OPTIONAL_MULTI;
}

export function allowsMultiplePartners(tipoOrRules) {
  const mode = getPartnerBindingMode(tipoOrRules);
  return mode === PARTNER_BINDING_MODE.REQUIRED_MULTI || mode === PARTNER_BINDING_MODE.OPTIONAL_MULTI;
}

export function requiresCheckin(tipoOrRules) {
  const rules = tipoOrRules?.regras ? tipoOrRules.regras : tipoOrRules;
  const policy = normalizeTipoRules(rules).checkPolicy;
  return policy === CHECK_POLICY.CHECKIN_ONLY || policy === CHECK_POLICY.CHECKIN_AND_CHECKOUT;
}

export function requiresCheckout(tipoOrRules) {
  const rules = tipoOrRules?.regras ? tipoOrRules.regras : tipoOrRules;
  const policy = normalizeTipoRules(rules).checkPolicy;
  return policy === CHECK_POLICY.CHECKOUT_ONLY || policy === CHECK_POLICY.CHECKIN_AND_CHECKOUT;
}

export function getStepPolicy(tipoOrRules, key) {
  const rules = tipoOrRules?.regras ? tipoOrRules.regras : tipoOrRules;
  return normalizeTipoRules(rules)[key];
}

export function isStepEnabled(stepPolicy) {
  return (stepPolicy?.mode || STEP_MODE.DISABLED) === STEP_MODE.ENABLED;
}

export function stepRequiresImage(stepPolicy) {
  return (stepPolicy?.imageRequirement || IMAGE_REQUIREMENT.OPTIONAL) === IMAGE_REQUIREMENT.REQUIRED;
}

export function stepRequiresApproval(stepPolicy) {
  return (stepPolicy?.approvalMode || APPROVAL_MODE.AUTOMATIC) === APPROVAL_MODE.ADMIN_APPROVAL;
}

export function getFieldMode(configOrMotivo, code) {
  const config = configOrMotivo?.campos ? configOrMotivo.campos : configOrMotivo;
  return normalizeMotivoFields(config).fields[code] || FIELD_MODE.HIDDEN;
}

export function isFieldVisible(mode) {
  return mode === FIELD_MODE.OPTIONAL || mode === FIELD_MODE.REQUIRED;
}

export function isFieldRequired(mode) {
  return mode === FIELD_MODE.REQUIRED;
}

export function mergeFieldModes(motivos = []) {
  const merged = { ...createDefaultMotivoFields().fields };
  motivos.forEach((motivo) => {
    const fields = normalizeMotivoFields(motivo?.campos).fields;
    Object.values(TASK_FIELD_CODES).forEach((code) => {
      const current = merged[code];
      const next = fields[code] || FIELD_MODE.HIDDEN;
      if (current === FIELD_MODE.REQUIRED || next === FIELD_MODE.REQUIRED) {
        merged[code] = FIELD_MODE.REQUIRED;
      } else if (current === FIELD_MODE.OPTIONAL || next === FIELD_MODE.OPTIONAL) {
        merged[code] = FIELD_MODE.OPTIONAL;
      } else {
        merged[code] = FIELD_MODE.HIDDEN;
      }
    });
  });
  return merged;
}

export function hasVisibleField(fieldModes, codes = Object.values(TASK_FIELD_CODES)) {
  return codes.some((code) => isFieldVisible(fieldModes?.[code]));
}

export function describePartnerBinding(mode) {
  return PARTNER_BINDING_OPTIONS.find((item) => item.value === mode)?.label || "Nao definido";
}

export function describeCheckPolicy(policy) {
  return CHECK_POLICY_OPTIONS.find((item) => item.value === policy)?.label || "Nao definido";
}

export function describeStepPolicy(stepPolicy) {
  const step = stepPolicy || {};
  if (!isStepEnabled(step)) return "Desabilitado";
  const image = stepRequiresImage(step) ? "imagem obrigatoria" : "imagem opcional";
  const approval = stepRequiresApproval(step) ? "aprovacao admin" : "automatico";
  return `${image} / ${approval}`;
}

export function describeFieldMode(mode) {
  return FIELD_MODE_OPTIONS.find((item) => item.value === mode)?.label || "Oculto";
}
